/**
 * NotificationRow — presentational sub-component for a single notification.
 *
 * Renders one `<li>` with an inner `<button type="button">` for the InboxItem.
 * Forwards a ref to the inner button for keyboard-driven DOM focus
 * (Arrow Up/Down in the parent `<NotificationsInbox>` calls `rowRef.focus()`).
 *
 * Read/unread visual states are conveyed via font-weight and an optional
 * unread indicator dot. The component is pure — no hooks, no side effects.
 *
 * @see specs/prd.md Section 3 FR-008
 */
import React, { forwardRef, type CSSProperties } from "react";
import type { InboxItem } from "../../api/types/notification";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NotificationRowProps {
  /** The notification view-model to render. */
  item: InboxItem;

  /** Called when the user activates (clicks or presses Enter on) this row. */
  onActivate: (item: InboxItem) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const listItemStyle: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
};

const buttonBaseStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  width: "100%",
  padding: "10px 14px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
  fontSize: "inherit",
  color: "inherit",
};

const unreadIndicatorStyle: CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "var(--color-sora, #3b82f6)",
  flexShrink: 0,
  marginTop: "6px",
};

const readIndicatorStyle: CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "transparent",
  flexShrink: 0,
  marginTop: "6px",
};

const contentStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const titleUnreadStyle: CSSProperties = {
  fontWeight: 600,
  margin: 0,
  fontSize: "14px",
  lineHeight: "1.3",
};

const titleReadStyle: CSSProperties = {
  fontWeight: 400,
  margin: 0,
  fontSize: "14px",
  lineHeight: "1.3",
  opacity: 0.75,
};

const metaStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  opacity: 0.6,
  marginTop: "2px",
};

const bodyStyle: CSSProperties = {
  margin: "2px 0 0",
  fontSize: "13px",
  opacity: 0.7,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Single notification row. The forwarded ref targets the inner `<button>`
 * so the parent can call `.focus()` for keyboard navigation.
 */
export const NotificationRow = forwardRef<HTMLButtonElement, NotificationRowProps>(
  function NotificationRow({ item, onActivate }, ref) {
    const handleClick = () => {
      onActivate(item);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onActivate(item);
      }
    };

    // Truncate body to 140 chars for display
    const displayBody =
      item.body != null && item.body.length > 140
        ? `${item.body.slice(0, 140)}...`
        : item.body;

    return (
      <li style={listItemStyle}>
        <button
          ref={ref}
          type="button"
          className="notif-row-button"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          aria-label={`${item.kindLabel}: ${item.title}${item.read ? "" : " (unread)"}`}
          data-testid={`notification-row-${item.id}`}
          style={buttonBaseStyle}
        >
          {/* Unread indicator dot */}
          <span
            style={item.read ? readIndicatorStyle : unreadIndicatorStyle}
            aria-hidden="true"
          />

          {/* Content */}
          <div style={contentStyle}>
            <p style={item.read ? titleReadStyle : titleUnreadStyle}>
              {item.title}
            </p>
            {displayBody != null && (
              <p style={bodyStyle}>{displayBody}</p>
            )}
            <div style={metaStyle}>
              <span>{item.kindLabel}</span>
              <span aria-hidden="true">&middot;</span>
              <span>{item.relativeTime}</span>
            </div>
          </div>
        </button>
      </li>
    );
  },
);
